<template>
  <div
    class="template-editor d-flex flex-column h-100 bg-bgDefault"
    :class="{ 'opacity-50 pointer-events-none': isLoading }"
  >
    <TemplateEditorTitle />

    <div class="d-flex flex-grow-1 overflow-hidden">
      <HideRbac :permissions="['listApprovalTemplates']">
        <TemplateEditorList
          class="w-25 flex-shrink-0"
          :templates="templates"
          :selected-ulid="selectedUlid"
          :has-unsaved="hasUnsavedTemplate"
          @select="handleSelect"
          @create="handleCreate"
        />
      </HideRbac>

      <TemplateEditorContent
        v-if="selectedTemplate"
        :template="selectedTemplate"
        :save-disabled="!isDirty"
        :is-new="!selectedUlid"
        class="w-75"
        @save="handleSave"
        @duplicate="handleDuplicate"
        @archive="handleArchive"
        @rename="handleRename"
        @add-stage="handleAddStage"
        @remove-stage="handleRemoveStage"
        @rename-stage="handleRenameStage"
        @add-slot="handleAddSlot"
        @remove-slot="handleRemoveSlot"
      />

      <div
        v-else
        class="w-75 d-flex align-center justify-center text-textLighter text-body-2"
      >
        Select a template to edit
      </div>
    </div>

    <TemplateUnsavedModal
      @stay="handleModalStay"
      @discard="handleModalDiscard"
      @save-and-switch="handleModalSaveAndSwitch"
    />

    <TemplateDeleteModal @delete="handleDelete" />
    <TemplateStageDeleteModal @delete="handleStageDelete" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';
import { useModals } from '@/stores/modals/modals';
import { useTracking } from '@/stores/tracking/tracking';
import HideRbac from '@/components/rbac/HideRbac.vue';
import {
  listTemplates, getTemplate, createTemplate, updateTemplate, deleteTemplate,
} from '@/api/approvalTemplates';
import TemplateEditorTitle from './TemplateEditorTitle.vue';
import TemplateEditorList from './TemplateEditorList.vue';
import TemplateEditorContent from './TemplateEditorContent.vue';
import TemplateUnsavedModal from './TemplateUnsavedModal.vue';
import TemplateDeleteModal from './TemplateDeleteModal.vue';
import TemplateStageDeleteModal from './TemplateStageDeleteModal.vue';

defineOptions({ name: 'TemplateEditor' });

const { addErrorSnackbar } = useAppSnackbar();
const appLoading = useAppLoading();
const { pageLoading, listLoading } = storeToRefs(appLoading);
const { setLoading } = appLoading;
const { setModal } = useModals();
const { modalParams } = storeToRefs(useModals());

const templates = ref([]);
const selectedUlid = ref(null);
const selectedTemplate = ref(null);
const savedSnapshot = ref('');

let pendingCallback = null;

const isLoading = computed(() => pageLoading.value || listLoading.value);

const isDirty = computed(() => {
  if (!selectedTemplate.value) return false;
  return JSON.stringify(selectedTemplate.value) !== savedSnapshot.value;
});

const hasUnsavedTemplate = computed(() => selectedUlid.value === null && selectedTemplate.value !== null);

const guardedAction = (callback) => {
  if (isDirty.value) {
    pendingCallback = callback;
    setModal({ field: 'templateUnsaved', value: true });
  } else {
    callback();
  }
};

const loadTemplates = async () => {
  setLoading({ listLoading: true });
  try {
    const data = await listTemplates();
    templates.value = data.templates;
  } catch (e) {
    addErrorSnackbar({ text: `Failed to load templates — ${e.message}` });
    throw e;
  } finally {
    setLoading({ listLoading: false });
  }
};

const loadSelectedTemplate = async (ulid) => {
  setLoading({ pageLoading: true });
  try {
    const { template } = await getTemplate(ulid);
    selectedTemplate.value = template;
    savedSnapshot.value = JSON.stringify(template);
  } catch (e) {
    addErrorSnackbar({ text: `Failed to load template — ${e.message}` });
    throw e;
  } finally {
    setLoading({ pageLoading: false });
  }
};

const doSelect = async (ulid) => {
  selectedUlid.value = ulid;
  await loadSelectedTemplate(ulid);
};

const handleSelect = (ulid) => {
  if (selectedUlid.value === ulid && !hasUnsavedTemplate.value) return;
  guardedAction(() => doSelect(ulid));
};

const doCreate = () => {
  const newTemplate = {
    ulid: null,
    name: 'New Template',
    description: '',
    stages: [{ stage_number: 1, name: 'Stage 1', slots: [{ name: 'New Slot', description: '' }] }],
    created_at: new Date().toISOString(),
    created_by: null,
    is_archived: false,
  };
  templates.value = [newTemplate, ...templates.value.filter((t) => t.ulid !== null)];
  selectedUlid.value = null;
  selectedTemplate.value = newTemplate;
  savedSnapshot.value = '';
};

const handleCreate = () => {
  guardedAction(doCreate);
};

const handleSave = async () => {
  setLoading({ pageLoading: true });
  try {
    if (!selectedUlid.value) {
      await createTemplate({
        name: selectedTemplate.value.name,
        description: selectedTemplate.value.description,
        stages: selectedTemplate.value.stages,
      });
      selectedUlid.value = null;
      selectedTemplate.value = null;

      useTracking().approvalTemplateCreated();
    } else {
      await updateTemplate(selectedUlid.value, {
        name: selectedTemplate.value.name,
        description: selectedTemplate.value.description,
        stages: selectedTemplate.value.stages,
      });

      useTracking().approvalTemplateSaved();
    }
    savedSnapshot.value = JSON.stringify(selectedTemplate.value);
    await loadTemplates();
  } catch (e) {
    addErrorSnackbar({ text: `Failed to save template — ${e.message}` });
    throw e;
  } finally {
    setLoading({ pageLoading: false });
  }
};

const handleDuplicate = async () => {
  try {
    await createTemplate({
      name: `${selectedTemplate.value.name} (copy)`,
      description: selectedTemplate.value.description,
      stages: selectedTemplate.value.stages,
    });

    useTracking().approvalTemplateDuplicated();
    await loadTemplates();
  } catch (e) {
    addErrorSnackbar({ text: `Failed to duplicate template — ${e.message}` });
    throw e;
  }
};

const handleArchive = async (isArchived) => {
  if (!selectedUlid.value) return;
  setLoading({ pageLoading: true });
  try {
    await updateTemplate(selectedUlid.value, {
      name: selectedTemplate.value.name,
      description: selectedTemplate.value.description,
      stages: selectedTemplate.value.stages,
      is_archived: isArchived,
    });
    if (isArchived) {
      useTracking().approvalTemplateArchived();

      selectedTemplate.value = null;
      selectedUlid.value = null;
      savedSnapshot.value = '';
    } else {
      selectedTemplate.value.is_archived = false;
      savedSnapshot.value = JSON.stringify(selectedTemplate.value);
    }
    await loadTemplates();
  } catch (e) {
    addErrorSnackbar({ text: `Failed to ${isArchived ? 'archive' : 'unarchive'} template — ${e.message}` });
    throw e;
  } finally {
    setLoading({ pageLoading: false });
  }
};

const handleDelete = async () => {
  if (!selectedUlid.value) return;
  setLoading({ pageLoading: true });
  try {
    await deleteTemplate(selectedUlid.value);
    selectedTemplate.value = null;
    selectedUlid.value = null;
    savedSnapshot.value = '';
    await loadTemplates();
  } catch (e) {
    addErrorSnackbar({ text: `Failed to delete template — ${e.message}` });
    throw e;
  } finally {
    setLoading({ pageLoading: false });
  }
};

const handleRename = ({ name, description }) => {
  selectedTemplate.value.name = name;
  selectedTemplate.value.description = description;
};

const handleAddStage = () => {
  useTracking().approvalTemplateAddedStage();

  const nextNumber = (selectedTemplate.value.stages.length ?? 0) + 1;
  selectedTemplate.value.stages.push({
    stage_number: nextNumber,
    name: `Stage ${nextNumber}`,
    slots: [{ name: 'New Slot', description: '' }],
  });
};

const handleRemoveStage = (stageNumber) => {
  if (selectedTemplate.value.stages.length > 1) {
    setModal({ field: 'deleteApprovalStage', value: true, params: { stageNumber } });
  }
};

const handleStageDelete = () => {
  const { stageNumber } = modalParams.value.deleteApprovalStage ?? {};
  if (stageNumber == null) return;
  useTracking().approvalTemplateRemovedStage();
  selectedTemplate.value.stages = selectedTemplate.value.stages
    .filter((s) => s.stage_number !== stageNumber)
    .map((s, i) => ({ ...s, stage_number: i + 1 }));
};

const handleRenameStage = (stageNumber, name) => {
  const stage = selectedTemplate.value.stages.find((s) => s.stage_number === stageNumber);
  if (stage) stage.name = name;
};

const handleAddSlot = (stageNumber) => {
  const stage = selectedTemplate.value.stages.find((s) => s.stage_number === stageNumber);
  if (stage) stage.slots.push({ name: 'New Slot', description: '' });
};

const handleRemoveSlot = (stageNumber, slotIndex) => {
  const stage = selectedTemplate.value.stages.find((s) => s.stage_number === stageNumber);
  if (stage && stage.slots.length > 1) stage.slots.splice(slotIndex, 1);
};

// Unsaved-changes modal handlers
const handleModalStay = () => {
  pendingCallback = null;
};

const handleModalDiscard = () => {
  if (!selectedUlid.value) {
    templates.value = templates.value.filter((t) => t.ulid !== null);
  }
  selectedTemplate.value = null;
  selectedUlid.value = null;
  savedSnapshot.value = '';
  const cb = pendingCallback;
  pendingCallback = null;
  cb?.();
};

const handleModalSaveAndSwitch = async () => {
  await handleSave();
  const cb = pendingCallback;
  pendingCallback = null;
  cb?.();
};

onMounted(loadTemplates);
</script>
